import { storage } from 'app/lib/storage';
import { StorageKey } from 'app/lib/storageKeys';

export interface SessionState {
  accessToken: string | null;
  refreshToken: string | null;
  currentUserId: string | null;
  loginAsUserId: string | null;
  loginAsEmail: string | null;
}

const EMPTY_SESSION: SessionState = {
  accessToken: null,
  refreshToken: null,
  currentUserId: null,
  loginAsUserId: null,
  loginAsEmail: null,
};

let sessionState: SessionState | undefined;
let hydrationPromise: Promise<SessionState> | undefined;
let revision = 0;

export async function hydrateSessionState(): Promise<SessionState> {
  if (sessionState) return sessionState;
  if (hydrationPromise) return hydrationPromise;

  const hydrationRevision = revision;
  const currentHydration = Promise.all([
    storage.readString(StorageKey.AccessToken),
    storage.readString(StorageKey.RefreshToken),
    storage.readString(StorageKey.CurrentUserId),
    storage.readString(StorageKey.LoginAsUserId),
    storage.readString(StorageKey.LoginAsEmail),
  ]).then(([accessToken, refreshToken, currentUserId, loginAsUserId, loginAsEmail]) => {
    if (revision === hydrationRevision) {
      sessionState = { accessToken, refreshToken, currentUserId, loginAsUserId, loginAsEmail };
    }
    return sessionState ?? EMPTY_SESSION;
  });
  hydrationPromise = currentHydration;

  try {
    return await currentHydration;
  } finally {
    if (hydrationPromise === currentHydration) hydrationPromise = undefined;
  }
}

export function updateSessionState(update: Partial<SessionState>): SessionState {
  revision += 1;
  sessionState = { ...EMPTY_SESSION, ...sessionState, ...update };
  return sessionState;
}

export function clearSessionState(): void {
  revision += 1;
  sessionState = { ...EMPTY_SESSION };
}

export function invalidateSessionState(): void {
  revision += 1;
  sessionState = undefined;
  hydrationPromise = undefined;
}

export function getLoginAsIdentifier(state: SessionState): string | null {
  return state.loginAsUserId ?? state.loginAsEmail;
}
